import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import COLORS from '../../theme/colors';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || 
              sessionStorage.getItem('redirectAfterLogin') || 
              '/';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      
      // Check for pending actions
      const pendingActionStr = sessionStorage.getItem('pendingAction');
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      
      // Clear stored values
      sessionStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('pendingAction');
      
      if (pendingActionStr) {
        try {
          const pendingAction = JSON.parse(pendingActionStr);
          console.log('Login: Found pending action:', pendingAction);
          
          if (pendingAction.type === 'ADD_TO_CART') {
            // Redirect back to the product page with a flag to execute the cart action
            navigate(redirectPath || from, { 
              replace: true,
              state: { 
                executePendingAction: true,
                pendingAction: pendingAction
              }
            });
            return;
          }
        } catch (error) {
          console.error('Error parsing pending action:', error);
        }
      }
      
      // Default redirect
      navigate(redirectPath || from, { replace: true });
    } catch (error) {
      setError(error.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.background.light }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 4, boxShadow: '0 2px 16px rgba(44, 62, 80, 0.08)', maxWidth: 400, width: '100%' }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: COLORS.text.primary, textAlign: 'left' }}>Login</Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            margin="normal"
            sx={{ background: COLORS.background.paper, borderRadius: 2, mb: 2 }}
            InputProps={{ sx: { fontSize: 18, color: COLORS.text.primary } }}
            InputLabelProps={{ sx: { fontSize: 16, color: COLORS.text.secondary } }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            margin="normal"
            sx={{ background: COLORS.background.paper, borderRadius: 2, mb: 2 }}
            InputProps={{ sx: { fontSize: 18, color: COLORS.text.primary } }}
            InputLabelProps={{ sx: { fontSize: 16, color: COLORS.text.secondary } }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 1, mb: 2, background: COLORS.primary.main, color: COLORS.primary.contrastText, borderRadius: 3, fontWeight: 600, fontSize: 18, boxShadow: 'none', '&:hover': { background: COLORS.primary.dark } }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>
        </form>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Button
            variant="text"
            sx={{ color: COLORS.text.secondary, textTransform: 'none', fontSize: 15, p: 0, minWidth: 0 }}
            onClick={() => navigate('/forgot-password')}
          >
            Forgot Password
          </Button>
          <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
            No account?{' '}
            <Button
              variant="text"
              sx={{ color: COLORS.primary.main, textTransform: 'none', fontWeight: 600, fontSize: 15, p: 0, minWidth: 0 }}
              onClick={() => navigate('/register')}
            >
              Register
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
